import {
  FindingStatusSchema,
  FindingSeveritySchema,
} from '@terra/shared-schemas-orchestrator';
import type {
  InternalEnrichedFinding,
  InternalFindingStatus,
} from '../../types';
import { token } from '@terra/ui-styled-system';
import type { z } from 'zod';
import {
  formatCategoryForDisplay,
  FINDING_STATUS_DISPLAY_MAP,
} from '../../utils/category-utils';

type FindingSeverityType = z.infer<typeof FindingSeveritySchema>;

type FindingBadgeItem = {
  key: keyof InternalEnrichedFinding;
  label: string;
  color?: string;
};

export const statusDisplayMap: Record<InternalFindingStatus, string> =
  FINDING_STATUS_DISPLAY_MAP;

export const statusColors: Record<InternalFindingStatus, string> = {
  [FindingStatusSchema.enum.Resolved]: 'terra.9',
  [FindingStatusSchema.enum.Unresolved]: 'terra.12',
  [FindingStatusSchema.enum.InProgress]: 'terra.10',
  [FindingStatusSchema.enum.FalsePositive]: 'terra.8',
  [FindingStatusSchema.enum.WontFix]: 'terra.7',
  [FindingStatusSchema.enum.Retest]: 'terra.11',
};

// Export severity color map for cases where a single color is needed (e.g., charts, icons)
// Use border color as it provides good contrast
export const severityColorMap: Record<FindingSeverityType, string> = {
  [FindingSeveritySchema.enum.Critical]: token(
    'colors.border.severity.critical',
  ),
  [FindingSeveritySchema.enum.High]: token('colors.border.severity.high'),
  [FindingSeveritySchema.enum.Medium]: token('colors.border.severity.medium'),
  [FindingSeveritySchema.enum.Low]: token('colors.border.severity.low'),
  [FindingSeveritySchema.enum.Info]: token('colors.neutral.6'),
};

export const calculateFindingBadges = (
  finding: InternalEnrichedFinding,
): FindingBadgeItem[] => {
  const severityBadge: FindingBadgeItem = {
    key: 'severity',
    label: finding.severity,
    // No color needed - EntityDrawerHeader will use SeverityBadge component
  };

  const statusBadge: FindingBadgeItem = {
    key: 'status',
    label: statusDisplayMap[finding.status],
    color: 'gray',
  };

  const categoryBadge: FindingBadgeItem | null = finding.category
    ? {
        key: 'category',
        label: formatCategoryForDisplay(finding.category),
        color: 'gray',
      }
    : null;

  return [
    severityBadge,
    statusBadge,
    ...(categoryBadge ? [categoryBadge] : []),
  ];
};
